import type { ISODateTime } from '@bigint-project/core';

import {
  captureProjectTemplate,
  deserializeProjectTemplate,
  instantiateProjectTemplate,
  PROJECT_TEMPLATE_KIND,
  serializeProjectTemplate,
  type ProjectTemplate,
  type ProjectTemplateCaptureMode,
} from '../../persistence/project-template';
import { deserializeProject, serializeProject } from '../../persistence/serialization';
import { captureCurrentViewSnapshot, restoreViewSnapshot } from '../../persistence/view-snapshot';
import { useProjectStore } from '../../store/project-store';
import type { BrowserProjectController, BrowserProjectSnapshot } from './browser-platform';
import { BPROJT_EXTENSION } from './file-api';

export interface BrowserTemplateExportInput {
  readonly captureMode: ProjectTemplateCaptureMode;
  readonly name: string;
  readonly description?: string;
}

export interface BrowserTemplateCreateInput {
  readonly projectName: string;
  readonly anchorDate?: ISODateTime;
}

export interface StoreBrowserProjectControllerOptions {
  readonly confirm?: (message: string) => boolean;
}

/** 文案對齊 desktop `confirmDiscardChanges()` 的動作標籤。 */
const ACTION_LABELS = {
  new: '建立新專案',
  open: '開啟其他專案',
  import: '匯入其他檔案',
} as const;

/** 副檔名被改掉時仍以信封的 kind 判斷，否則範本會被當成 `.bproj` 解析失敗。 */
function isTemplateFile(contents: string, sourceName: string): boolean {
  if (sourceName.toLowerCase().endsWith(BPROJT_EXTENSION)) return true;
  try {
    return (JSON.parse(contents) as { readonly kind?: unknown } | null)?.kind === PROJECT_TEMPLATE_KIND;
  } catch {
    return false;
  }
}

function downloadName(name: string): string {
  return name.trim().replace(/[\\/:*?"<>|]/g, '_') || 'Untitled Project';
}

export function createStoreBrowserProjectController(
  options: StoreBrowserProjectControllerOptions = {},
): BrowserProjectController<ProjectTemplate, BrowserTemplateExportInput, BrowserTemplateCreateInput> {
  const confirm = options.confirm ?? ((message: string) => window.confirm(message));

  return {
    confirmDiscardChanges(action) {
      if (!useProjectStore.getState().file.isDirty) return true;
      const label = ACTION_LABELS[action];
      return confirm(`目前專案有未儲存的變更，確定要${label}嗎？`);
    },
    createNewProject() {
      useProjectStore.getState().newProject();
    },
    loadProjectText(contents, sourceName) {
      if (isTemplateFile(contents, sourceName)) {
        // 與桌面「從範本建立專案」同一個對話框；建立時走 createProjectFromTemplate。
        useProjectStore.getState().openTemplateCreate(deserializeProjectTemplate(contents));
        return;
      }
      const parsed = deserializeProject(contents);
      useProjectStore.getState().loadProject(parsed.project, null, {
        unknownTopLevelFields: parsed.unknownTopLevelFields,
        unknownViewFields: parsed.unknownViewFields,
      });
      if (parsed.view !== null) restoreViewSnapshot(parsed.view);
    },
    captureProject(): BrowserProjectSnapshot {
      const state = useProjectStore.getState();
      return {
        contents: serializeProject(state.project, captureCurrentViewSnapshot(), undefined, {
          unknownTopLevelFields: state.file.unknownTopLevelFields,
          unknownViewFields: state.file.unknownViewFields,
        }),
        suggestedName: downloadName(state.project.name),
      };
    },
    captureProjectTemplate(input): BrowserProjectSnapshot {
      const template = captureProjectTemplate(useProjectStore.getState().project, {
        captureMode: input.captureMode,
        metadata: { name: input.name, description: input.description },
      });
      return {
        contents: serializeProjectTemplate(template),
        suggestedName: downloadName(template.metadata.name),
      };
    },
    createProjectFromTemplate(template, input) {
      if (useProjectStore.getState().hostPolicy.readOnly) return;
      const project = instantiateProjectTemplate(template, input);
      useProjectStore.getState().loadProject(project, null);
    },
    isDirty() {
      return useProjectStore.getState().file.isDirty;
    },
  };
}
